import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import {
  ProductTypeDetailDto,
  ProductTypeDto
} from 'src/store/producttypes/producttype';
import { mapProductTypeDetailToEdit } from 'src/store/producttypes/producttype.map';
import {
  createProductType,
  deleteProductType,
  editProductType,
  fetchProductTypes
} from 'src/store/producttypes/producttypes.actions';
import { ProductTypeApi } from 'src/store/producttypes/producttypes.api';
import { selectProductTypes } from 'src/store/producttypes/producttypes.selectors';
import { isLoadingProductTypes } from 'src/store/requests/requests.selectors';
import { ProductTypeForm } from './ProductTypeForm';

export const ProductTypesPage = () => {
  const [currentProductType, setCurrentProductType] =
    useState<ProductTypeDto | null>(null);
  const [productType, setProductType] =
    useState<Nullable<ProductTypeDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const productTypes = useSelector(selectProductTypes);
  const loadingProductTypes = useSelector(isLoadingProductTypes);

  useEffect(() => {
    dispatch(fetchProductTypes());
  }, [dispatch]);

  useEffect(() => {
    if (currentProductType) {
      setShowDeleteModal(true);
    }
  }, [currentProductType]);

  const onCancel = () => {
    setCurrentProductType(null);
    setProductType(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (productType: ProductTypeDto) => {
    const response = await ProductTypeApi.getById(productType.id);
    if (response.ok) {
      setProductType(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (productType: ProductTypeDto) => {
    setCurrentProductType(productType);
  };

  const onConfirmDelete = () => {
    if (currentProductType) {
      dispatch(deleteProductType(currentProductType.id, currentProductType));
      setCurrentProductType(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (productType: ProductTypeDetailDto) => {
    const dto = mapProductTypeDetailToEdit(productType);
    if (dto.id) {
      await dispatch(editProductType(dto.id, dto));
      onCancel();
    } else {
      await dispatch(createProductType(dto));
      onCancel();
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('productType.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('productType.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('productType.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={productTypes}
        loading={loadingProductTypes}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'no', dir: 'asc' }]}
      >
        <Column
          field="no"
          width={200}
          title={t('props.number')}
          footerCell={() => (
            <GridCountCell
              count={productTypes?.length}
              label={t('productType.plural')}
            />
          )}
        />
        <Column field="name" title={t('props.name')} />
        <Column field="description" title={t('props.description')} />
        <Column
          width="160px"
          field="active"
          title={t('props.active')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => <BooleanFilterCell {...props} />}
        />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        header={`${productType ? t('edit') : t('create.verb')} ${t(
          'productType.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <ProductTypeForm
          productType={productType}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('productType.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('productType.delete')} <q>{currentProductType?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
